/**
 * NegotiationBlueprintPage — tactical read for one specific negotiation.
 * Sales-native + consent-free: counterpart(s) are saved Profiles (Full via
 * direct-DOB). Single or pair mode; optional your-side unlocks the Dynamic.
 */

import { useState } from "react";
import { useSession } from "../lib/useSession.js";
import {
  Wordmark, SectionHeader, Callout, AuditFooter, MarkdownV2, Chip,
  PrintToPdfButton, RecentBlueprintsPanel,
} from "../components/index.js";
import MentionAutocomplete from "../components/MentionAutocomplete.jsx";
import BlueprintChat from "../components/BlueprintChat.jsx";
import { parseJsonResponse } from "../lib/api-fetch.js";
import { terminalActionButtonStyle, terminalActionButtonDisabledStyle } from "../lib/buttonStyles.js";

const NEGOTIATION_TYPES = [
  { value: "price",          label: "Price" },
  { value: "contract_terms", label: "Contract terms" },
  { value: "partnership",    label: "Partnership / deal" },
  { value: "renewal",        label: "Renewal" },
  { value: "vendor",         label: "Vendor / procurement" },
  { value: "offer",          label: "Offer (you're extending)" },
  { value: "other",          label: "Other" },
];

const PRESETS = [
  { label: "What's their real walk-away?",        preset_text: "What's this counterpart's real walk-away point, and what signals it?" },
  { label: "Draft my opening number",             preset_text: "Given how this person anchors, what opening number or position should I lead with?" },
  { label: "Best concession to trade first",      preset_text: "What's the first concession I should offer, and what should I extract for it?" },
  { label: "If they go silent",                   preset_text: "If this counterpart goes silent or cold mid-negotiation, what's the read and the move?" },
];

export default function NegotiationBlueprintPage() {
  const { session } = useSession();

  const [counterpartMentions, setCounterpartMentions] = useState([]);
  const [counterpartText, setCounterpartText] = useState("");
  const [selfMentions, setSelfMentions] = useState([]);
  const [selfText, setSelfText] = useState("");
  const [negType, setNegType] = useState("price");
  const [ctx, setCtx] = useState({
    what_is_being_negotiated: "", your_goal: "", your_walkaway: "",
    their_likely_batna: "", constraints: "", history: "",
  });

  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [blueprint, setBlueprint] = useState(null);

  const generate = async () => {
    if (counterpartMentions.length < 1) {
      setError("Mention at least one counterpart (a saved Profile).");
      return;
    }
    if (counterpartMentions.length > 2) {
      setError("Up to two counterparts. For a bigger group, use the Room Blueprint.");
      return;
    }
    setError(null);
    setGenerating(true);
    try {
      const res = await fetch("/.netlify/functions/negotiation-blueprint", {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          counterpart_profile_ids: counterpartMentions.map((m) => m.profile_id),
          your_side_profile_id: selfMentions[0]?.profile_id || null,
          deal_context: ctx,
          negotiation_type: negType,
        }),
      });
      const data = await parseJsonResponse(res);
      if (!res.ok) {
        setError(data.error || `Generation failed (HTTP ${res.status})`);
        setGenerating(false);
        return;
      }
      setBlueprint(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  if (!session) return <p style={{ padding: 24 }}>Sign in to generate a Negotiation Blueprint.</p>;

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: "32px 24px" }}>
      <div style={{ marginBottom: 24 }}><Wordmark scale={1.0} /></div>

      {!blueprint ? (
        <div>
          <RecentBlueprintsPanel type="negotiation" bearerToken={session.access_token} />
          <SectionHeader index={1} title="New Negotiation Blueprint" />

          <Callout.Tip>
            Read the person across the table before you sit down — what they're really optimizing for, where their give is, how they concede, and your move sequence. Mention one counterpart, or two for a pair on the other side.
          </Callout.Tip>

          <div style={{ display: "grid", gap: 16, marginTop: 20 }}>
            <Field label={`Counterpart(s) (mention with @) — ${counterpartMentions.length} so far`}>
              <MentionAutocomplete
                value={counterpartText}
                onChange={setCounterpartText}
                mentions={counterpartMentions}
                onMentionsChange={setCounterpartMentions}
                placeholder="@Dana  (add a 2nd for a pair on their side)"
                rows={2}
                bearerToken={session.access_token}
              />
            </Field>

            <Field label="What are you negotiating?">
              <select value={negType} onChange={(e) => setNegType(e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
                {NEGOTIATION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>

            <Field label="What's on the table?">
              <input value={ctx.what_is_being_negotiated} onChange={(e) => setCtx({ ...ctx, what_is_being_negotiated: e.target.value })} placeholder="Annual renewal + 12% uplift" style={inputStyle} />
            </Field>

            <Field label="Your goal">
              <input value={ctx.your_goal} onChange={(e) => setCtx({ ...ctx, your_goal: e.target.value })} placeholder="Land the uplift, keep the relationship warm" style={inputStyle} />
            </Field>

            <Field label="Your walk-away / floor (optional, high-value)">
              <input value={ctx.your_walkaway} onChange={(e) => setCtx({ ...ctx, your_walkaway: e.target.value })} placeholder="Flat renewal, no discount past 5%" style={inputStyle} />
            </Field>

            <Field label="Their likely alternative / BATNA (optional)">
              <input value={ctx.their_likely_batna} onChange={(e) => setCtx({ ...ctx, their_likely_batna: e.target.value })} placeholder="Shop a competitor, or status quo" style={inputStyle} />
            </Field>

            <Field label="Constraints (optional)">
              <textarea value={ctx.constraints} onChange={(e) => setCtx({ ...ctx, constraints: e.target.value })} rows={2} placeholder="Deadline, budget, approvals, relationship stakes" style={inputStyle} />
            </Field>

            <Field label="History / context (optional)">
              <textarea value={ctx.history} onChange={(e) => setCtx({ ...ctx, history: e.target.value })} rows={3} placeholder="Prior rounds, emails, what's been offered" style={inputStyle} />
            </Field>

            <Field label="Your profile (optional) — adds a head-to-head dynamic read">
              <MentionAutocomplete
                value={selfText}
                onChange={setSelfText}
                mentions={selfMentions}
                onMentionsChange={setSelfMentions}
                placeholder="@You — mention your own saved profile"
                rows={2}
                bearerToken={session.access_token}
              />
            </Field>

            {error && <ErrorBox error={error} />}

            <button
              onClick={generate}
              disabled={generating}
              style={generating ? terminalActionButtonDisabledStyle : terminalActionButtonStyle}
            >
              {generating ? "Generating…" : "Generate Negotiation Blueprint"}
            </button>
          </div>
        </div>
      ) : (
        <article>
          <h1 style={titleStyle}>{blueprint.title || "Negotiation Blueprint"}</h1>

          {Array.isArray(blueprint.card_sections) && (
            <div style={{ display: "grid", gap: 10, margin: "18px 0 28px" }}>
              {blueprint.card_sections.map((c, i) => (
                <div key={i} style={cardStyle}>
                  <div style={cardLabelStyle}>{c.label}</div>
                  <div style={cardBodyStyle}>{c.body}</div>
                </div>
              ))}
            </div>
          )}

          <MarkdownV2
            source={blueprint.blueprint_markdown}
            subjectNames={counterpartMentions.map((m) => m.display_name).filter(Boolean)}
          />

          <BlueprintChat
            blueprintId={blueprint.blueprint_id}
            blueprintType="negotiation"
            presetPrompts={PRESETS}
            bearerToken={session.access_token}
          />

          <AuditFooter
            engineVersion={blueprint.engine_version}
            jargonScanPassed={true}
            blueprintId={blueprint.blueprint_id}
          />

          <div style={{ marginTop: 24, display: "flex", justifyContent: "space-between" }}>
            <button onClick={() => setBlueprint(null)} style={{ ...terminalActionButtonStyle, background: "transparent", color: "var(--ppr-color-accent-navy)" }}>
              ← New blueprint
            </button>
            <PrintToPdfButton label="Save as PDF" />
          </div>
        </article>
      )}
    </div>
  );
}

const inputStyle = {
  padding: "12px 14px",
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontSize: "var(--ppr-typography-font-size-base)",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
  color: "var(--ppr-color-text-primary)",
  width: "100%",
  outline: "none",
  resize: "vertical",
};

const titleStyle = {
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-4xl)",
  color: "var(--ppr-color-accent-navy)",
  letterSpacing: "var(--ppr-typography-letter-spacing-tight)",
  lineHeight: "var(--ppr-typography-line-height-tight)",
  margin: "0 0 8px",
};

const cardStyle = {
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
  padding: "12px 14px",
};
const cardLabelStyle = {
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-sm)",
  letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
  textTransform: "uppercase",
  color: "var(--ppr-color-accent-coral)",
  marginBottom: 4,
};
const cardBodyStyle = {
  fontFamily: "var(--ppr-typography-font-family-body)",
  fontSize: "var(--ppr-typography-font-size-base)",
  color: "var(--ppr-color-text-primary)",
  lineHeight: 1.5,
};

function Field({ label, children }) {
  return (
    <div>
      <label style={{
        display: "block",
        fontFamily: "var(--ppr-typography-font-family-display)",
        fontSize: "var(--ppr-typography-font-size-sm)",
        letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
        textTransform: "uppercase",
        color: "var(--ppr-color-text-secondary)",
        marginBottom: 6,
      }}>{label}</label>
      {children}
    </div>
  );
}

function ErrorBox({ error }) {
  return (
    <div style={{
      padding: "12px 14px",
      border: "2px solid var(--ppr-color-accent-coral)",
      background: "var(--ppr-color-accent-coral-very-soft)",
      color: "var(--ppr-color-accent-coral-dark)",
    }}>
      {error}
    </div>
  );
}
